import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatError, MatFormField, MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatOption, MatSelect } from '@angular/material/select';
import { MatTooltip } from '@angular/material/tooltip';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { NgxPaginationModule } from 'ngx-pagination';
import { NgxSpinnerService } from 'ngx-spinner';
import { Subject, takeUntil } from 'rxjs';
import { ApiServiceService } from '../api-service.service';
import { CommonPaginationComponent } from '../common-component-pagination/common-component-pagination.component';
import { CommonSearchComponent } from '../common-component-search/common-component-search.component';
import { SharedValidators } from '../shared/validators/shared-validators';

@Component({
  selector: 'app-dealer-master',
  templateUrl: './dealer-master.component.html',
  styleUrls: ['./dealer-master.component.scss'],
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatIcon,
    NgxPaginationModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatError,
    MatTooltip,
    CommonSearchComponent,
    CommonPaginationComponent,
    CommonModule,
    MatSelect,
    NgxMatSelectSearchModule,
    MatOption,
  ],
})
export class DealerMasterComponent implements OnInit, OnDestroy {
  private _onDestroy = new Subject<void>();

  size = 10;
  page = 1;
  searchText = '';
  totalLength = 0;
  dealerList: {
    _id: string;
    dealercode: string;
    posCode: string;
    posName: string;
    mobile: string;
    state: string;
    city: string;
    email: string;
  }[] = [];

  exportData = [];

  userPermission = [];
  dealerMasterCreate = false;
  dealerMasterUpdate = false;
  dealerMasterDelete = false;
  dealerMasterView = false;

  dealerMasterForm: FormGroup = this.formBuilder.group({});
  dealerMasterSearchForm: FormGroup = this.formBuilder.group({});
  addNewDealer = false;

  editMode = false;
  selectedDealerId = '';

  stateNameFilterCtrl: FormControl = new FormControl();
  stateList: { name: string; value: string }[] = [];
  filteredListOfState: { name: string; value: string }[] = [];

  isDownloading = false;

  // storage listener for cross-tab sync
  private storageListener = (event: StorageEvent) => {
    if (event.key === 'downloadInProgress') {
      this.isDownloading = event.newValue === 'true';
    }
  };

  constructor(
    private formBuilder: FormBuilder,
    public apiMicroservice: ApiServiceService,
    private spinner: NgxSpinnerService
  ) {}

  ngOnInit() {
    this.setDealerMasterLogForm();
    this.checkPermission();
    if (this.dealerMasterView) {
      this.getAllDealersData();
      this.syncInitialDownloadState();
    }
  }

  ngOnDestroy() {
    this._onDestroy.next();
    this._onDestroy.complete();
  }

  checkPermission() {
    this.userPermission = JSON.parse(
      this.apiMicroservice.decryptData(
        sessionStorage.getItem('userPermission') || '[]'
      )
    );

    this.userPermission.forEach(
      (element: {
        pageName: string;
        view: boolean;
        create: boolean;
        delete: boolean;
        update: boolean;
      }) => {
        if (element.pageName === 'Dealer Master' && element.view === false) {
          this.navigateBack();
        }
        if (element.pageName === 'Dealer Master' && element.view === true) {
          this.dealerMasterView = true;
        }
        if (element.pageName === 'Dealer Master' && element.create === true) {
          this.dealerMasterCreate = true;
        }
        if (element.pageName === 'Dealer Master' && element.update === true) {
          this.dealerMasterUpdate = true;
        }
        if (element.pageName === 'Dealer Master' && element.delete === true) {
          this.dealerMasterDelete = true;
        }
      }
    );
  }

  syncInitialDownloadState() {
    // sync initial download state across tabs
    this.isDownloading = localStorage.getItem('downloadInProgress') === 'true';
    window.addEventListener('storage', this.storageListener);
  }

  setDealerMasterLogForm() {
    this.dealerMasterForm = this.formBuilder.group({
      dealercode: new FormControl('', [
        SharedValidators.requiredAndNoEmptySpaces,
      ]),
      posCode: new FormControl('', [SharedValidators.requiredAndNoEmptySpaces]),
      posName: new FormControl('', [SharedValidators.requiredAndNoEmptySpaces]),
      mobile: new FormControl('', [SharedValidators.mobileNumberValidation]),
      state: new FormControl('', [SharedValidators.requiredAndNoEmptySpaces]),
      city: new FormControl('', [SharedValidators.requiredAndNoEmptySpaces]),
      email: new FormControl('', [
        SharedValidators.requiredAndNoEmptySpaces,
        SharedValidators.validEmail,
      ]),
    });
    this.dealerMasterSearchForm = this.formBuilder.group({
      searchDealer: new FormControl(''),
    });
  }

  // Fetch all dealers from API
  getAllDealersData() {
    this.spinner.show();
    this.apiMicroservice
      .getAllDealers(this.page, this.size, this.searchText)
      .subscribe((res) => {
        this.dealerList = res.data.data;
        this.totalLength = res.data.totalItems;
        this.apiMicroservice.openSnackbar('Fetched Successfully');
        this.spinner.hide();
      });
  }

  save() {
    if (this.dealerMasterForm.invalid) {
      SharedValidators.markAllAsTouched(this.dealerMasterForm);
    } else {
      this.saveDealer();
    }
  }

  // Save dealer - Handles both Add and Edit scenarios
  saveDealer() {
    const payload = this.dealerMasterForm.value;
    if (this.editMode && this.selectedDealerId) {
      // Update existing dealer
      this.apiMicroservice
        .updateDealerData(this.selectedDealerId, payload)
        .subscribe((res) => {
          this.handleResponse(res);
        });
    } else {
      // Add new dealer
      this.apiMicroservice.saveDealerData(payload).subscribe((res) => {
        this.handleResponse(res);
      });
    }
  }

  // View and edit dealer details
  prePopulation(dealerId: string) {
    if (dealerId) {
      this.spinner.show();
      this.addNewDealer = true;
      this.editMode = true;
      this.selectedDealerId = dealerId;
      this.apiMicroservice.getDealerById(dealerId).subscribe((res) => {
        if (res.status) {
          this.spinner.hide();
          this.dealerMasterForm.patchValue(res.data[0]);
          this.getStateListStateWiseAssign();
        }
      });
    } else {
      this.setDealerMasterLogForm();
      this.getStateListStateWiseAssign();
    }
  }

  // Show add dealer form
  addNewDealerFunc() {
    this.addNewDealer = true;
    this.editMode = false;
    this.getStateListStateWiseAssign();
  }

  getStateListStateWiseAssign() {
    this.apiMicroservice.getAllGovtServerList().subscribe((res) => {
      if (res.status) {
        const stateList = res.data;

        this.stateList = stateList.map(
          (element: { state: string; stateAbbreviation: string }) => ({
            name: element.state,
            value: element.stateAbbreviation,
          })
        );
        this.filteredListOfState = this.stateList.slice();

        this.stateNameFilterCtrl = new FormControl();
        this.stateNameFilterCtrl.valueChanges
          .pipe(takeUntil(this._onDestroy))
          .subscribe((search: string) => {
            if (!search) {
              this.stateList = [...this.stateList];
              return;
            }

            // Filter the list based on search value
            search = search.toLowerCase();
            this.stateList = this.stateList.filter((item) =>
              item.name.toLowerCase().includes(search)
            );
          });
      }
    });
  }

  // Cancel and hide add/edit form
  cancelFunc() {
    this.addNewDealer = false;
    this.selectedDealerId = '';
    this.resetForm();
  }

  onPageChange(event: number) {
    this.page = event;
    this.getAllDealersData();
  }

  onSizeChange(event: number) {
    this.page = 1;
    this.size = event;
    this.getAllDealersData();
  }

  onSearch(event: string) {
    this.page = 1;
    this.searchText = event;
    this.dealerMasterSearchForm
      ?.get('searchDealer')
      ?.setValue(this.searchText ? this.searchText : '');
    this.getAllDealersData();
  }

  // Reset the form and edit mode
  resetForm() {
    this.dealerMasterForm.reset();
    this.dealerMasterForm = this.formBuilder.group({
      dealercode: new FormControl('', [
        SharedValidators.requiredAndNoEmptySpaces,
      ]),
      posCode: new FormControl('', [SharedValidators.requiredAndNoEmptySpaces]),
      posName: new FormControl('', [SharedValidators.requiredAndNoEmptySpaces]),
      mobile: new FormControl('', [SharedValidators.mobileNumberValidation]),
      state: new FormControl('', [SharedValidators.requiredAndNoEmptySpaces]),
      city: new FormControl('', [SharedValidators.requiredAndNoEmptySpaces]),
      email: new FormControl('', [
        SharedValidators.requiredAndNoEmptySpaces,
        SharedValidators.validEmail,
      ]),
      searchDealer: new FormControl(''),
    });
    this.editMode = false;
    this.addNewDealer = false;
    this.selectedDealerId = '';
  }

  // Delete dealer
  deleteDealer(id: string) {
    if (confirm('Are you sure?')) {
      this.spinner.show();
      this.apiMicroservice.deleteDealerData(id).subscribe((res) => {
        if (res.status) {
          this.getAllDealersData();
          this.spinner.hide();
        }
      });
    }
  }

  handleResponse(res: { status: boolean; message: string }) {
    if (res.status) {
      this.apiMicroservice.openSnackbar(res.message);
      this.resetForm();
      this.getAllDealersData();
    } else {
      this.apiMicroservice.openSnackbar(res.message);
    }
  }

  downloadDealerMasterData() {
    if (confirm('Are you sure?')) {
      // mark download in-progress and notify other tabs
      this.isDownloading = true;
      localStorage.setItem('downloadInProgress', 'true');
      this.apiMicroservice.openSnackbar(
        'Your file is being generated. Refreshing the page or logging out may interrupt the download.'
      );
      this.searchText = this.dealerMasterSearchForm?.get('searchDealer')?.value;
      this.apiMicroservice.downloadDealerMasterData(this.searchText).subscribe({
        next: (res) => {
          try {
            if (res?.status && res.fileDownloadUrl) {
              const link = document.createElement('a');
              link.href = res.fileDownloadUrl;
              link.download = ''; // let browser infer filename
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);

              this.apiMicroservice.openSnackbar('File ready for download');
            } else {
              this.apiMicroservice.openSnackbar(
                res?.message || 'File URL not available'
              );
            }
          } finally {
            // clear in-progress state
            this.isDownloading = false;
            localStorage.setItem('downloadInProgress', 'false');
          }
        },
        error: (ERROR: HttpErrorResponse) => {
          const example =
            ERROR.error.message ||
            'Download failed. Please contact server team.';
          this.apiMicroservice.openSnackbar(example);
          // clear in-progress state
          this.isDownloading = false;
          localStorage.setItem('downloadInProgress', 'false');
        },
      });
    }
  }

  navigateBack() {
    this.apiMicroservice.back();
  }
}
